import { Form, Formik, useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import useProperties from "../../../hooks/use-properties";
import {
  ADD_TENANT_INITIAL_VALUE,
  convertTenantCivilSchema,
  convertTenantLCDSchema,
  convertTenantMobiliteSchema,
  convertTenantSchema,
  LETTING_FEES_INITIAL_VALUE,
  Tenant,
  TenantStatus,
} from "../../../models/tenant_old.js";
import { ClauseType } from "../../../utils/constants/clauses";
import Callout from "../../UI/Callout";
import TenantConvertForm from "./TenantConvertForm";
import { useNavigate } from "react-router-dom";
import {
  dateMax,
  sanitizeValues,
  toInitialValuesFromSchema,
  toNestedStructure,
} from "../../../models/utils";
import PropertyDisplaySmallCard from "../../Property/PropertyDisplaySmallCard";
import useTenants from "../../../hooks/use-tenants";
import useResource from "../../../hooks/use-resource";
import TabbedForm from "../../UI/TabbedForm";
import {
  handleAPIError,
  showFormikFieldsInError,
} from "../../../utils/index.jsx";
import useLeaseTemplates from "../../../hooks/use-lease-templates";
import ChooseSendFileMethod from "../../UI/ChooseSendFileMethod";
import TenantAddProfile from "../TenantAddProfile";
import {
  mandatairesFields,
  Profile,
  ProfileStatus,
} from "../../../models/profile";
import useMandataire from "../../../hooks/use-mandataire";
import ClausesForm from "./ClausesForm";
import Button from "../../UI/Button";
import { Lease } from "../../../models/leaseTemplate";
import { ReactSelect } from "../../UI/Select";
import TextInput from "../../UI/TextInput";
import useLoader from "../../../hooks/use-loader";
import ProfileForm from "../../Profile/ProfileForm";
import RepresentantForm from "../../Profile/RepresentantForm";
import GenBailChoice from "./GenBailChoice";
import TenantAddFormDocument from "./TenantAddFormDocument";
import { TenantLeaseType } from "../../../utils/constants/tenants";
import { PropertyStatus } from "../../../models/property";
import DocumentDownloadLink from "../../UI/DocumentDownloadLink";
import DocumentThumbnail from "../../UI/DocumentThumbnail";
import TenantProfileList from "../../Profile/TenantProfileList";
import { produce } from "immer";
import useProfileBailleurs from "../../../hooks/use-profile-bailleurs";
import { lazy, object, string } from "yup";

import useAuth from "../../../hooks/use-auth.js";
import styles from "./TenantAddForm.module.css";

export default function TenantAddForm({
  tenant,
  modal,
  propertyId: propertyIdProp,
  isRegenerate,
  isCandidatOnly = false,
}) {
  const { isPro } = useAuth();
  const { mandataire } = useMandataire();
  const { propertiesById } = useProperties({ withById: true });
  const { createTenant, recreateTenant, convertTenant, createCandidat } =
    useTenants();
  const { leaseTemplatesById, isLoadingLeaseTemplates, leaseTemplateError } =
    useLeaseTemplates({
      withById: true,
    });
  const { profileBailleursById } = useProfileBailleurs({
    withById: true,
  });
  const [previewBail, setPreviewBail] = useState("");
  const [error, setError] = useState("");
  const nav = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const structProfiles = useMemo(
    () =>
      toNestedStructure(
        profiles,
        ["tmp_garantor_of", "tmp_legal_representative_of"],
        ["garantors", "legal_representatives"],
        "tmp_id",
      ),
    [profiles],
  );
  const tmpProfileIdRef = useRef(1);

  useEffect(() => {
    if (tenant) {
      tmpProfileIdRef.current = 1;
      const newProfiles = [];
      tenant.profiles
        .filter(
          (profile) =>
            profile.garantor_of === null &&
            profile.status === ProfileStatus.Actif.status,
        )
        .forEach((profile) => {
          const newProfile = { ...profile };
          newProfile.tmp_id = tmpProfileIdRef.current++;
          newProfiles.push(newProfile);
          for (let garantor of newProfile.garantors) {
            const newGarantor = { ...garantor };
            newGarantor.tmp_id = tmpProfileIdRef.current++;
            newGarantor.tmp_garantor_of = newProfile.tmp_id;
            newProfiles.push(newGarantor);
          }
          for (let representant of newProfile.legal_representatives ?? []) {
            const newRepresentant = { ...representant };
            newRepresentant.tmp_id = tmpProfileIdRef.current++;
            newRepresentant.tmp_legal_representative_of = newProfile.tmp_id;
            newProfiles.push(newRepresentant);
          }
        });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProfiles(newProfiles);
    }
  }, [tenant]);

  if (isLoadingLeaseTemplates || leaseTemplateError) return null;

  function getDureeContrat(leaseTemplateId, profileBailleurId) {
    const leaseTemplate = leaseTemplatesById[leaseTemplateId];
    if (!leaseTemplate) {
      return "";
    }

    const profileBailleur = profileBailleursById[profileBailleurId];
    if (
      profileBailleur &&
      leaseTemplate.type === TenantLeaseType.NU &&
      profileBailleur?.is_societe &&
      !profileBailleur?.sci_famille
    ) {
      return 72;
    }
    return leaseTemplate.duree;
  }

  async function submitHandler(values, preview = false) {
    if (profiles.length === 0) {
      setError(
        "Veuillez sélectionner au moins un profil locataire avant de continuer",
      );
      return;
    }
    setError("");

    const sanitizedValues = sanitizeValues(values);

    if (isCandidatOnly) {
      try {
        const newTenant = await createCandidat(
          sanitizedValues.id_bien,
          structProfiles,
          sanitizedValues.debut,
        );
        nav(`/bailleur/tenants/${newTenant.id}`);
      } catch (error) {
        setError(handleAPIError(error));
      }
      return;
    }

    const generatePayments = sanitizedValues.loyer_a_jour;
    const typeEnvoi = sanitizedValues.type_envoi;
    const generateBail = sanitizedValues.genBail === "true";

    const newTenant = Tenant.create(sanitizedValues);
    newTenant.status = TenantStatus.Actif.status;

    const lease = Lease.create(sanitizedValues);

    try {
      const otherValues = {
        generate_bail: generateBail,
        generate_payments: generatePayments,
        generate_initial_balance: sanitizedValues.generate_initial_balance,
        type_envoi: typeEnvoi,
        preview,
        lease,
        iban: sanitizedValues.iban,
        iban_titulaire: sanitizedValues.iban_titulaire,
      };
      let newTenantOrPreview;
      if (isRegenerate) {
        otherValues.generate_bail = true;
        newTenantOrPreview = await recreateTenant(
          tenant.id,
          newTenant,
          structProfiles,
          otherValues,
        );
      } else if (tenant?.status === TenantStatus.Candidat.status) {
        newTenantOrPreview = await convertTenant(
          tenant.id,
          newTenant,
          structProfiles,
          otherValues,
        );
      } else {
        newTenantOrPreview = await createTenant(
          newTenant,
          structProfiles,
          otherValues,
        );
      }
      if (preview) {
        setPreviewBail(newTenantOrPreview);
        return;
      }
      const tenantId = newTenantOrPreview.id;
      const garantPhysique = structProfiles.some(
        (profile) =>
          profile.caution_type?.toLowerCase() === "physique" &&
          profile.garantors?.length > 0,
      );
      const navParams = new URLSearchParams();
      if (generateBail) {
        navParams.set("tab-tenant-show", "documents-courriers");
      }
      if ((generateBail || isRegenerate) && garantPhysique) {
        navParams.set("caution-reminder", "1");
      }
      const navQuery = navParams.toString();
      nav(`/bailleur/tenants/${tenantId}${navQuery ? `?${navQuery}` : ""}`);
    } catch (error) {
      setError(handleAPIError(error));
    }
  }

  const initialPropertyId = tenant?.id_bien ?? propertyIdProp;
  const initialProperty = propertiesById[initialPropertyId];

  const validationSchema = lazy((values) => {
    if (isCandidatOnly) {
      return object({
        id_bien: string().required("Veuillez sélectionner un bien"),
        debut: dateMax(),
      });
    }

    const leaseType = leaseTemplatesById[values.id_lease_template]?.type;

    let schema = convertTenantSchema;
    if (leaseType === TenantLeaseType.MOBILITE) {
      schema = convertTenantMobiliteSchema;
    } else if (
      leaseType === TenantLeaseType.CIVIL ||
      leaseType === TenantLeaseType.CIVIL_HABITATION ||
      leaseType === TenantLeaseType.STOCKAGE ||
      leaseType === TenantLeaseType.GARAGE ||
      leaseType === TenantLeaseType.PARKING
    ) {
      schema = convertTenantCivilSchema;
    } else if (leaseType === TenantLeaseType.LCD) {
      schema = convertTenantLCDSchema;
    }
    if (isPro) {
      schema = schema.concat(mandatairesFields);
    }

    return schema;
  });

  const initialValues = toInitialValuesFromSchema(
    validationSchema.resolve({
      value: {
        id_lease_template: initialProperty?.id_lease_template,
      },
    }),
    tenant,
    ADD_TENANT_INITIAL_VALUE,
  );
  initialValues.join_notice = true;
  initialValues.join_guide_reparations = true;

  if (!isRegenerate) {
    initialValues.id_bien = initialPropertyId?.toString() ?? "";
    initialValues.loyer_hc = initialProperty?.loyer_hc ?? "";
    initialValues.dernier_loyer = initialProperty?.dernier_loyer ?? "";
    initialValues.charges = initialProperty?.charges ?? "";
    initialValues.deposit = initialProperty?.deposit ?? "";
    initialValues.day_payment = initialProperty?.day_payment ?? "";
    initialValues.type_charge = initialProperty?.type_charge ?? "";
    initialValues.debut = tenant?.debut ?? "";
    initialValues.detail_charges = initialProperty?.detail_charges ?? [];
    initialValues.id_compte_tenant = tenant?.id_compte_tenant ?? null;
    initialValues.loyer_a_jour = false;
    initialValues.article_specific = initialProperty?.article_specific ?? "";
    initialValues.complement_loyer = initialProperty?.complement_loyer ?? "";
    initialValues.justif_complement_loyer =
      initialProperty?.justif_complement_loyer ?? "";
    initialValues.generate_initial_balance = true;
    initialValues.autre_appel = initialProperty?.autre_appel ?? [];
    initialValues.tva = initialProperty?.tva ?? false;
    initialValues.taux_tva = initialProperty?.taux_tva ?? "";
    initialValues.tva_charge = initialProperty?.tva_charge ?? false;
    initialValues.taux_tva_charge = initialProperty?.taux_tva_charge ?? "";
    initialValues.is_triennal = tenant?.is_triennal ?? false;
    initialValues.iban = initialProperty?.iban ?? "";
    initialValues.iban_titulaire = initialProperty?.iban_titulaire ?? "";
  } else {
    initialValues.generate_initial_balance = false;
    // tenant.loyer_hc inclut le complément fondu à la génération du bail :
    // on ré-affiche le découpage d'origine pour ne pas le fondre une seconde fois.
    if (tenant?.complement_loyer) {
      initialValues.loyer_hc =
        Math.round((tenant.loyer_hc - tenant.complement_loyer) * 100) / 100;
    }
    initialValues.complement_loyer = tenant?.complement_loyer ?? "";
    initialValues.justif_complement_loyer =
      tenant?.justif_complement_loyer ?? "";
  }
  initialValues.echeance_locataire = "anytime";
  initialValues.echeance_bailleur = "anytime";
  initialValues.type_envoi = "elec";
  initialValues.id_lease_template = initialProperty?.id_lease_template ?? "";
  initialValues.duree_contrat = getDureeContrat(
    initialProperty?.id_lease_template,
    initialProperty?.id_profile_nom_owner,
  );
  initialValues.article_specific = initialProperty?.article_specific ?? "";
  initialValues.genBail =
    tenant?.status === TenantStatus.Candidat.status || isRegenerate
      ? "true"
      : "";
  initialValues.clauses_specifiques =
    initialProperty?.clauses_specifiques ?? [];
  initialValues.letting_fees = Object.fromEntries(
    Object.keys(LETTING_FEES_INITIAL_VALUE).map((key) => [
      key,
      tenant?.letting_fees?.[key] ?? "",
    ]),
  );

  if (isPro) {
    // Une agence facture ses honoraires de mise en location : la clause est proposée cochée
    if (!initialValues.clauses_specifiques.includes(ClauseType.HONORAIRE)) {
      initialValues.clauses_specifiques = [
        ...initialValues.clauses_specifiques,
        ClauseType.HONORAIRE,
      ];
    }
    initialValues.remuneration_type = mandataire?.remuneration_type ?? "";
    initialValues.remuneration_montant = mandataire?.remuneration_montant ?? "";
    initialValues.mandataire_tva = mandataire?.tva ?? false;
  }

  const innerProps = {
    canSelectProperty: !initialPropertyId,
    submitHandler,
    isRegenerate,
    isCandidatOnly,
    modal,
    leaseTemplatesById,
    getDureeContrat,
    error,
    setError,
    previewBail,
    setPreviewBail,
    profiles,
    structProfiles,
    setProfiles,
    tmpProfileIdRef,
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={(values) => submitHandler(values)}
      >
        <InnerForm {...innerProps} />
      </Formik>
    </>
  );
}

function InnerForm({
  canSelectProperty,
  submitHandler,
  isRegenerate,
  isCandidatOnly,
  modal: ModalElement,
  leaseTemplatesById,
  getDureeContrat,
  error,
  setError,
  previewBail,
  setPreviewBail,
  profiles,
  structProfiles,
  setProfiles,
  tmpProfileIdRef,
}) {
  const {
    validateForm,
    setFieldTouched,
    setFieldValue,
    resetForm,
    values,
    dirty,
  } = useFormikContext();
  const leaseTemplate = leaseTemplatesById[values.id_lease_template];

  const { properties, propertiesById, isLoadingProperties, propertiesError } =
    useProperties({ withById: true });
  const formRef = useRef();

  const [isProfileFormOpen, setIsProfileFormOpen] = useState(false);
  const [profileIdToEdit, setProfileIdToEdit] = useState(null);
  const [profileIdToGarantee, setProfileIdToGarantee] = useState(null);
  const [profileIdToRepresent, setProfileIdToRepresent] = useState(null);

  function openCreateProfileForm() {
    setProfileIdToEdit(null);
    setProfileIdToGarantee(null);
    setProfileIdToRepresent(null);
    setIsProfileFormOpen(true);
  }
  function openEditProfileForm(profileId) {
    setProfileIdToEdit(profileId);
    setProfileIdToGarantee(null);
    setProfileIdToRepresent(null);
    setIsProfileFormOpen(true);
  }
  function openAddGarantForm(parentProfileId) {
    setProfileIdToEdit(null);
    setProfileIdToGarantee(parentProfileId);
    setProfileIdToRepresent(null);
    setIsProfileFormOpen(true);
  }
  function openAddRepresentantForm(parentProfileId) {
    setProfileIdToEdit(null);
    setProfileIdToGarantee(null);
    setProfileIdToRepresent(parentProfileId);
    setIsProfileFormOpen(true);
  }
  function closeProfileForm() {
    setIsProfileFormOpen(false);
    setProfileIdToEdit(null);
    setProfileIdToGarantee(null);
    setProfileIdToRepresent(null);
  }

  // tmp_ids des locataires représentés : ils ne signent pas, leurs représentants légaux signent à leur place.
  const representedTmpIds = new Set(
    profiles
      .filter((p) => p.tmp_legal_representative_of)
      .map((p) => p.tmp_legal_representative_of),
  );

  const editingProfile = profiles.find((p) => p.tmp_id === profileIdToEdit);

  const propertyId = parseInt(values.id_bien);
  const selectedProperty = propertiesById[propertyId];
  const { profileBailleursById } = useProfileBailleurs({
    withById: true,
  });
  const profileBailleur =
    profileBailleursById[selectedProperty?.id_profile_nom_owner];

  const { resource: dernierLoyerInfo } = useResource(
    !isRegenerate && !isCandidatOnly && propertyId
      ? `biens/${propertyId}/dernier-loyer`
      : null,
  );
  // Pré-remplit une seule fois par bien : si l'utilisateur vide le champ
  // ensuite, on ne le re-remplit pas.
  const dernierLoyerPrefilledRef = useRef(null);
  useEffect(() => {
    if (
      !dernierLoyerInfo ||
      !leaseTemplate?.habitation ||
      dernierLoyerPrefilledRef.current === propertyId
    ) {
      return;
    }
    dernierLoyerPrefilledRef.current = propertyId;
    if (!values.dernier_loyer && dernierLoyerInfo.dernier_loyer != null) {
      setFieldValue("dernier_loyer", dernierLoyerInfo.dernier_loyer);
    }
    if (!values.date_dernier_loyer && dernierLoyerInfo.date_dernier_loyer) {
      setFieldValue("date_dernier_loyer", dernierLoyerInfo.date_dernier_loyer);
    }
  }, [
    dernierLoyerInfo,
    leaseTemplate,
    propertyId,
    setFieldValue,
    values.dernier_loyer,
    values.date_dernier_loyer,
  ]);

  const [isPreviewLoading, previewBailHandler] = useLoader(
    async function previewBailHandler(e, values) {
      setPreviewBail("");
      setError("");
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        showFormikFieldsInError(errors, setFieldTouched);
        return;
      }

      try {
        await submitHandler(values, true);
      } catch (error) {
        setError(handleAPIError(error));
      }
    },
  );

  if (isLoadingProperties || propertiesError) return null;

  const propertyOptions = properties
    .filter((p) => p.status === PropertyStatus.Actif.status)
    .map((p) => ({
      label: p.nom + (p.abonnement_actif ? "" : " (bien hors abonnement)"),
      value: p.id,
      isDisabled: !p.abonnement_actif,
    }));

  function propertyChangeHandler(option) {
    resetForm();
    const prop = propertiesById[option?.value];
    return leaseChangeHandler(
      prop?.id_lease_template,
      prop?.id_profile_nom_owner,
    );
  }

  function leaseChangeHandler(leaseTemplateId, profileBailleurId) {
    return setFieldValue(
      "duree_contrat",
      getDureeContrat(leaseTemplateId, profileBailleurId),
    );
  }

  function updateProfile(profile, profileId) {
    setProfiles((profiles) => {
      return produce(profiles, (draft) => {
        const index = draft.findIndex((p) => p.tmp_id === profileId);
        if (index !== -1) {
          draft[index] = profile;
        }
      });
    });
  }

  function addProfile(profile) {
    const newProfile = { ...profile };
    newProfile.tmp_id = tmpProfileIdRef.current++;
    newProfile.garantor_of = null;
    newProfile.garantors = [];
    newProfile.legal_representative_of = null;
    newProfile.legal_representatives = [];
    const newProfiles = [newProfile];
    for (let garantor of profile.garantors ?? []) {
      const newGarantor = Profile.create(garantor);
      newGarantor.tmp_id = tmpProfileIdRef.current++;
      newGarantor.tmp_garantor_of = newProfile.tmp_id;
      newGarantor.garantor_of = null;
      newProfiles.push(newGarantor);
    }
    for (let representant of profile.legal_representatives ?? []) {
      const newRepresentant = Profile.create(representant);
      newRepresentant.tmp_id = tmpProfileIdRef.current++;
      newRepresentant.tmp_legal_representative_of = newProfile.tmp_id;
      newRepresentant.legal_representative_of = null;
      newProfiles.push(newRepresentant);
    }
    setProfiles((profiles) => [...profiles, ...newProfiles]);
  }

  function profileSubmitHandler(data) {
    const profile = Profile.create(data);
    // Keep the tmp_ids if they exist
    profile.tmp_id = data.tmp_id;
    profile.tmp_garantor_of = data.tmp_garantor_of;
    profile.tmp_legal_representative_of = data.tmp_legal_representative_of;
    profile.status = ProfileStatus.Actif.status;
    if (profileIdToEdit !== null) {
      updateProfile(profile, profileIdToEdit);
    } else {
      if (profileIdToGarantee !== null) {
        profile.tmp_garantor_of = profileIdToGarantee;
      }
      if (profileIdToRepresent !== null) {
        profile.tmp_legal_representative_of = profileIdToRepresent;
      }
      addProfile(profile);
    }
    closeProfileForm();
  }

  return (
    <TabbedForm id="tenant-form" ref={formRef}>
      <ModalElement
        confirmCloseText={
          "Votre formulaire n'a pas été sauvegardé. Voulez-vous vraiment quitter ?"
        }
        shouldConfirm={() => dirty}
        size={"ultra"}
        stickyHeader
        blueHeader
        onClose={() => {
          resetForm();
          formRef.current?.resetTab();
        }}
        header={
          <TabbedForm.TabList>
            <TabbedForm.Tab panelId="bien">Bien</TabbedForm.Tab>
            <TabbedForm.Tab panelId="locataires">
              {isCandidatOnly ? "Candidat" : "Locataires"}
            </TabbedForm.Tab>
            {!isCandidatOnly && !isRegenerate && (
              <TabbedForm.Tab panelId="genBail">Action</TabbedForm.Tab>
            )}
            {!isCandidatOnly && values.genBail !== "" && (
              <TabbedForm.Tab panelId="bail">Bail</TabbedForm.Tab>
            )}
            {!isCandidatOnly && values.genBail === "true" && (
              <TabbedForm.Tab panelId="clauses">Clauses</TabbedForm.Tab>
            )}
            {!isCandidatOnly &&
              values.genBail === "true" &&
              leaseTemplate?.type !== TenantLeaseType.LCD && (
                <TabbedForm.Tab panelId="documents">Documents</TabbedForm.Tab>
              )}
            {!isCandidatOnly && values.genBail === "true" && (
              <TabbedForm.Tab panelId="validation">Envoi</TabbedForm.Tab>
            )}
          </TabbedForm.TabList>
        }
      >
        <Form>
          <TabbedForm.TabPanel id="bien">
            <div className={styles.tab}>
              {isRegenerate && (
                <Callout variant="info">
                  Vous pouvez utiliser cette fonction si vous avez fait une
                  erreur dans la génération initiale du bail. Pour tout
                  changement de locataire ou changement de montant du loyer,
                  veuillez passer par un avenant au bail. Rubrique locataire,
                  onglet "Actions / courriers", carte "Avenant au bail".
                </Callout>
              )}
              {canSelectProperty && (
                <ReactSelect
                  name="id_bien"
                  label={"Bien concerné"}
                  options={propertyOptions}
                  mandatory
                  onChange={propertyChangeHandler}
                />
              )}
              {selectedProperty && (
                <PropertyDisplaySmallCard
                  property={selectedProperty}
                  variant="stacked"
                />
              )}
              {!isCandidatOnly && profileBailleur?.is_societe === null && (
                <Callout variant="warning">
                  Vous n'avez pas renseigné si le bien est détenu par une
                  société ou un particulier. Cela a une influence sur la durée
                  du bail nu si vous êtes en société. Renseignez cette
                  information dans le menu "profil bailleur".
                </Callout>
              )}

              {isCandidatOnly && (
                <TextInput
                  type="date"
                  name="debut"
                  label={"Date d'entrée souhaitée"}
                />
              )}
            </div>
            <TabbedForm.Actions error={error} />
          </TabbedForm.TabPanel>
          <TabbedForm.TabPanel id="locataires">
            <div className={styles.tab}>
              {!isCandidatOnly && (
                <Callout variant="info">
                  Si plusieurs locataires sur le même bail, il faut créer /
                  sélectionner plusieurs profils locataires.
                </Callout>
              )}
              <p>
                {isCandidatOnly
                  ? "Ajoutez le(s) profil(s) du candidat"
                  : "Veuillez sélectionner le(s) locataire(s) à ajouter"}
                <span className={"blueStar"}> *</span>
              </p>
              <TenantAddProfile
                profiles={profiles}
                onCreate={openCreateProfileForm}
                onSelect={addProfile}
                variant="inline"
              />
              {isProfileFormOpen ? (
                editingProfile?.tmp_legal_representative_of != null ||
                profileIdToRepresent !== null ? (
                  <RepresentantForm
                    inline
                    profile={editingProfile}
                    onSubmit={profileSubmitHandler}
                    onCancel={closeProfileForm}
                  />
                ) : (
                  <ProfileForm
                    inline
                    profile={editingProfile}
                    onSubmit={profileSubmitHandler}
                    onCancel={closeProfileForm}
                    withOldAddressField
                  />
                )
              ) : (
                <TenantProfileList
                  minimalDisplay
                  profiles={structProfiles}
                  profilePrimaryKey={"tmp_id"}
                  editProfileHandler={openEditProfileForm}
                  addGarantProfileHandler={openAddGarantForm}
                  addRepresentantProfileHandler={openAddRepresentantForm}
                  deleteProfileHandler={(profileId) => {
                    setProfiles((curr) =>
                      curr.filter((p) => {
                        return (
                          p.tmp_id !== profileId &&
                          p.tmp_garantor_of !== profileId &&
                          p.tmp_legal_representative_of !== profileId
                        );
                      }),
                    );
                  }}
                  removeGarant={(profileId) => {
                    setProfiles((curr) =>
                      curr.filter((p) => p.tmp_id !== profileId),
                    );
                  }}
                  removeRepresentant={(profileId) => {
                    setProfiles((curr) =>
                      curr.filter((p) => p.tmp_id !== profileId),
                    );
                  }}
                />
              )}
            </div>
            {isCandidatOnly ? (
              <TabbedForm.Actions
                error={error}
                isLast
                disableNext={isProfileFormOpen}
                disableNextReason="Enregistrez ou fermez le profil locataire en cours avant de continuer"
              />
            ) : (
              <TabbedForm.Actions
                error={error}
                disableNext={isProfileFormOpen}
                disableNextReason="Enregistrez ou fermez le profil locataire en cours avant de continuer"
              />
            )}
          </TabbedForm.TabPanel>
          {!isCandidatOnly && !isRegenerate && (
            <TabbedForm.TabPanel id="genBail">
              <div className={"marginT20 marginB10"}>
                <GenBailChoice />
              </div>
              <TabbedForm.Actions
                error={error}
                disableNext={values.genBail === ""}
                disableNextReason="Choisissez une option pour continuer"
              />
            </TabbedForm.TabPanel>
          )}
          {!isCandidatOnly && values.genBail !== "" && (
            <TabbedForm.TabPanel id="bail" forceRenderInactive>
              <TenantConvertForm
                propertyType={selectedProperty?.type}
                propertyIndice={selectedProperty?.type_indice}
                propertyLoyerReference={selectedProperty?.loyer_reference}
                propertyLoyerRefMaj={selectedProperty?.montant_loyer_ref_maj}
                propertySurface={selectedProperty?.surface_total}
                onLeaseChange={(o) =>
                  leaseChangeHandler(
                    o?.value,
                    selectedProperty?.id_profile_nom_owner,
                  )
                }
                isRegenerate={isRegenerate}
              />
              {leaseTemplate?.habitation && !values.dernier_loyer && (
                <Callout variant="warning">
                  Vous avez laissé le champ "Montant du dernier loyer versé par
                  le locataire précédent" vide. Ce champ est obligatoire dès
                  lors que ce dernier a quitté le logement moins de dix-huit
                  mois avant la signature du bail.
                </Callout>
              )}
              <TabbedForm.Actions error={error} />
            </TabbedForm.TabPanel>
          )}
          {values.genBail === "true" && (
            <TabbedForm.TabPanel id="clauses" forceRenderInactive>
              <div className={styles.tab}>
                <ClausesForm leaseType={leaseTemplate?.type} />
                <Button
                  type={"button"}
                  onClick={(e) => previewBailHandler(e, values)}
                  isLoading={isPreviewLoading}
                >
                  Prévisualiser le bail
                </Button>
                {previewBail && (
                  <div className={"flex gap10 marginT10"}>
                    <DocumentDownloadLink
                      downloadUrl={previewBail}
                      mimetype={"application/pdf"}
                    >
                      <DocumentThumbnail
                        downloadUrl={previewBail}
                        mimetype={"application/pdf"}
                      />
                    </DocumentDownloadLink>
                  </div>
                )}
              </div>
              <TabbedForm.Actions error={error} />
            </TabbedForm.TabPanel>
          )}
          {!isCandidatOnly && (
            <TabbedForm.TabPanel id="documents">
              <TenantAddFormDocument isRegenerate={isRegenerate} />
              {leaseTemplate?.identifiant_fiscal &&
                !selectedProperty?.identifiant_fiscal && (
                  <Callout variant="warning">
                    Vous n'avez pas renseigné l'identifiant fiscal de votre
                    bien. Il est légalement obligatoire pour votre type de bail.
                    Vous pouvez l'ajouter en allant sur votre bien, et en
                    éditant la carte "description du bien".
                  </Callout>
                )}
              <TabbedForm.Actions error={error} />
            </TabbedForm.TabPanel>
          )}
          {values.genBail === "true" && (
            <TabbedForm.TabPanel id="validation">
              <ChooseSendFileMethod
                propertyId={parseInt(values.id_bien)}
                profiles={profiles.filter(
                  (p) =>
                    !p.tmp_garantor_of &&
                    !p.garantor_of &&
                    !representedTmpIds.has(p.tmp_id),
                )}
                error={error}
                onPreview={(previewValues) =>
                  previewBailHandler(null, previewValues)
                }
                previewUrl={previewBail}
              />
            </TabbedForm.TabPanel>
          )}
        </Form>
      </ModalElement>
    </TabbedForm>
  );
}
